import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

// Biconomy has NO browser CORS (api.biconomy.com returns no access-control-allow-origin),
// so every REST call MUST be routed through the proxy. We reuse `proxyAxios` (basic auth +
// proxy baseURL baked from env) and the same `/api/v1/proxy?url=<encoded target>` routing
// the shared SDK proxy client uses (src/shared/cex-sdk/lib/proxy-client.ts).
const BICONOMY_REST_BASE = 'https://api.biconomy.com';
const BICONOMY_PROXY_PATH = '/api/v1/proxy';
const BICONOMY_WS_URL = 'wss://bei.biconomy.com/ws';

// Biconomy depth.subscribe params are [symbol, limit, mergeInterval]; "0" = finest merge.
const BICONOMY_WS_DEPTH_LIMIT = 50;
const BICONOMY_WS_DEPTH_MERGE = '0';

// Biconomy expects the CLIENT to keep the WS alive with periodic server.ping.
const BICONOMY_WS_PING_INTERVAL = 20000;

// REST snapshot poll cadence (ms). Keeps panels populated even if the browser WS to
// Biconomy is blocked/unsupported.
const BICONOMY_REST_POLL_INTERVAL = 5000;

// Build the full target URL with query params and route it through the proxy, mirroring
// exchangeProxyClient.get() in src/shared/cex-sdk/lib/proxy-client.ts.
const proxyGet = <T>(url: string, params: Record<string, string | number> = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => search.append(key, String(value)));
  const query = search.toString();
  const targetUrl = query ? `${url}?${query}` : url;

  return proxyAxios.get<T>(BICONOMY_PROXY_PATH, { params: { url: targetUrl } });
};

// Biconomy REST candle `type` per page interval (mirror connector/biconomy/common/enum.go).
// Biconomy uses "hour"/"4hour"/"day"/"week" (no month type → fall back to week).
const BICONOMY_TYPE_BY_TIMEFRAME: Partial<Record<IChartTimeframe, string>> = {
  '1s': '1min',
  '10s': '1min',
  '30s': '1min',
  '1m': '1min',
  '3m': '5min',
  '5m': '5min',
  '15m': '15min',
  '30m': '30min',
  '1h': 'hour',
  '2h': 'hour',
  '4h': '4hour',
  '6h': '4hour',
  '8h': '4hour',
  '12h': '4hour',
  '24h': 'day',
  '1d': 'day',
  '2d': 'day',
  '3d': 'day',
  '5d': 'day',
  '168h': 'week',
  '1w': 'week',
  '1M': 'week',
};

// Biconomy WS kline.subscribe interval (SECONDS) per connectWebSocket interval (ms).
const BICONOMY_WS_KLINE_SECONDS_BY_INTERVAL_MS: Record<number, number> = {
  60000: 60,
  300000: 300,
  900000: 900,
  1800000: 1800,
  3600000: 3600,
  14400000: 14400,
  86400000: 86400,
  604800000: 604800,
};

// Biconomy exchangeInfo returns a string `tickSize` (e.g. "0.01"), not an integer
// precision. Derive the decimal-place count so the orderbook aggregation tick logic
// (shared with bitget) works.
const precisionFromTickSize = (tickSize: string | undefined): number => {
  if (!tickSize) return 1;
  const num = Number(tickSize);
  if (!Number.isFinite(num) || num <= 0) return 1;
  const [, decimals = ''] = tickSize.split('.');
  const trimmed = decimals.replace(/0+$/, '');
  return trimmed.length || 0;
};

export class BiconomyStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;
  pollInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  biconomySymbol: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.biconomySymbol = `${base.toUpperCase()}_${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      // Biconomy exchangeInfo is a bare array (no code/data wrapper).
      const res = await proxyGet<{ symbol: string; tickSize: string }[]>(
        `${BICONOMY_REST_BASE}/api/v1/exchangeInfo`,
      );

      let prec = 1;

      if (Array.isArray(res.data)) {
        const info = res.data.find(s => s.symbol?.toUpperCase() === this.biconomySymbol);
        prec = precisionFromTickSize(info?.tickSize);
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Biconomy ticker error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const type = BICONOMY_TYPE_BY_TIMEFRAME[interval] ?? '1min';
    const symbol = `${base.toUpperCase()}_${quote.toUpperCase()}`;

    try {
      // Biconomy candles use a `type` param + `size` count (no time range) and return a
      // bare array of [ts_ms, open, high, low, close, baseVolume].
      const result = await proxyGet<(string | number)[][]>(
        `${BICONOMY_REST_BASE}/api/v1/kline`,
        { symbol, type, size: 1000 },
      );

      // Biconomy returns ms timestamps; the chart pipeline (handleNewCharts) already
      // converts ms->seconds, so return ms unchanged like every other strategy.
      return (Array.isArray(result.data) ? result.data : []).map(el => [
        Number(el[0]),
        String(el[1]),
        String(el[2]),
        String(el[3]),
        String(el[4]),
        String(el[5]),
      ]) as KLine[];
    } catch (error) {
      console.log('Biconomy klines error: ', error);

      return [];
    }
  };

  // Live trades arrive via the WS `deals` channel and feed onTradeUpdate; the initial REST
  // snapshot is left empty (mirrors the WEEX strategy).
  fetchTrades = async (_args: { base: string; quote: string; limit: number }) => {
    return [] as TradeSocket[];
  };

  fetchDepthSnapshot = async () => {
    try {
      // Biconomy depth returns a bare { asks, bids } object (no code/data wrapper) with
      // [price, amount] string tuples.
      const res = await proxyGet<{ asks?: [string, string][]; bids?: [string, string][] }>(
        `${BICONOMY_REST_BASE}/api/v1/depth`,
        { symbol: this.biconomySymbol, size: BICONOMY_WS_DEPTH_LIMIT },
      );

      if (!res.data?.asks || !res.data?.bids) return;

      const responseAsks = res.data.asks ?? [];
      const responseBids = res.data.bids ?? [];

      this.asks = mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc).slice(0, this.depth);
      this.bids = mergeOrderBook(responseBids, []).sort(sortOrderbookFunc).slice(0, this.depth);

      this.publishOrderbook();
    } catch (error) {
      console.log('Biconomy depth snapshot error: ', error);
    }
  };

  fetchTickerSnapshot = async () => {
    try {
      // Biconomy has no per-symbol ticker endpoint — fetch all and filter by symbol.
      const res = await proxyGet<{
        ticker?: {
          symbol: string;
          last: string;
          high: string;
          low: string;
          vol: string;
          deal: string;
          change: string;
        }[];
      }>(`${BICONOMY_REST_BASE}/api/v1/tickers`);

      const ticker = res.data?.ticker?.find(t => t.symbol?.toUpperCase() === this.biconomySymbol);

      if (!ticker) return;

      const { last, high, low, vol, deal, change } = ticker;

      this.onTickerUpdate({
        baseVolume: vol,
        // Biconomy `change` is already a percentage (e.g. "-4.9").
        change: normalizeNumber(Number(change)),
        close: last,
        high,
        last: Number(last).toFixed(this.precision),
        low,
        quoteVolume: deal,
      });
    } catch (error) {
      console.log('Biconomy ticker snapshot error: ', error);
    }
  };

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(
        this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
        this.selectedPrecision,
      ),
    );
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket(BICONOMY_WS_URL, [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;

    const klineSeconds = BICONOMY_WS_KLINE_SECONDS_BY_INTERVAL_MS[interval] ?? 60;

    // REST snapshots so the panels populate immediately and stay fresh even if the
    // browser WS to Biconomy is blocked/unsupported.
    this.fetchDepthSnapshot();
    this.fetchTickerSnapshot();

    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    this.pollInterval = setInterval(() => {
      this.fetchDepthSnapshot();
      this.fetchTickerSnapshot();
    }, BICONOMY_REST_POLL_INTERVAL);

    // Biconomy WS keep-alive is client-initiated.
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ method: 'server.ping', params: [], id: 5160 }));
      }
    }, BICONOMY_WS_PING_INTERVAL);

    this.ws.onopen = () => {
      // CoinEx/ViaBTC JSON-RPC subscribe (one method per message).
      ws.send(JSON.stringify({ method: 'state.subscribe', params: [this.biconomySymbol], id: 1 }));
      ws.send(
        JSON.stringify({
          method: 'depth.subscribe',
          params: [this.biconomySymbol, BICONOMY_WS_DEPTH_LIMIT, BICONOMY_WS_DEPTH_MERGE],
          id: 2,
        }),
      );
      ws.send(JSON.stringify({ method: 'deals.subscribe', params: [this.biconomySymbol], id: 3 }));
      ws.send(
        JSON.stringify({
          method: 'kline.subscribe',
          params: [this.biconomySymbol, klineSeconds],
          id: 4,
        }),
      );
    };

    this.ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        // Subscription acks and pongs carry no `method` (only `result`/`id`) — ignore.
        if (!data || typeof data.method !== 'string') {
          return;
        }

        const params = data.params;

        // state.update: ["SYMBOL", {last, open, high, low, volume, deal}]
        if (data.method === 'state.update' && Array.isArray(params) && params.length >= 2) {
          const state = params[1] ?? {};
          const openNum = Number(state.open);
          const lastNum = Number(state.last);
          const changePct =
            Number.isFinite(openNum) && openNum !== 0 && Number.isFinite(lastNum)
              ? ((lastNum - openNum) / openNum) * 100
              : 0;

          this.onTickerUpdate({
            baseVolume: state.volume,
            change: normalizeNumber(changePct),
            close: state.last,
            high: state.high,
            last: Number(state.last).toFixed(this.precision),
            low: state.low,
            quoteVolume: state.deal,
          });
        }

        // kline.update: [[time_s, open, close, high, low, volume, deal, symbol], ...]
        if (data.method === 'kline.update' && Array.isArray(params) && params.length > 0) {
          const last = params[params.length - 1];
          // ViaBTC ordering: open, close, high, low. Reorder to KLine [t, o, h, l, c, v].
          const [time, open, close, high, low, volume] = last;

          this.klineUpdate([
            Number(time), // WS kline time is already in SECONDS
            String(open),
            String(high),
            String(low),
            String(close),
            String(volume),
          ]);
        }

        // depth.update: [isSnapshot bool, {asks, bids}, symbol]
        if (data.method === 'depth.update' && Array.isArray(params) && params.length >= 2) {
          const isSnapshot = params[0] === true;
          const book = params[1] ?? {};

          const responseAsks: [string, string][] = book.asks ?? [];
          const responseBids: [string, string][] = book.bids ?? [];

          const asks = isSnapshot
            ? mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc)
            : mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);

          const bids = isSnapshot
            ? mergeOrderBook(responseBids, []).sort(sortOrderbookFunc)
            : mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

          this.asks = asks.slice(0, this.depth);
          this.bids = bids.slice(0, this.depth);

          this.publishOrderbook();
        }

        // deals.update: ["SYMBOL", [{amount, time, id, type, price}, ...]]
        if (
          data.method === 'deals.update' &&
          Array.isArray(params) &&
          params.length >= 2 &&
          Array.isArray(params[1]) &&
          params[1].length > 0
        ) {
          const { time, price, amount, type } = params[1][0];

          this.onTradeUpdate({
            price,
            quantity: amount,
            // `time` is a Unix timestamp in SECONDS (float) -> ms string.
            timestamp: String(Math.round(Number(time) * 1000)),
            // `type` is the taker side directly.
            side: type === 'sell' ? 'sell' : 'buy',
          });
        }
      } catch (error) {
        console.log('Biconomy socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
      this.ws = undefined;
    }
    if (this.depthWs) {
      this.depthWs.close();
      this.depthWs = undefined;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = undefined;
    }
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = undefined;
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    // Biconomy depth WS is a fixed-level book with no server-side scaling, so the tick
    // switch is a pure client-side re-aggregation.
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth(depth: number) {
    this.depth = depth;
    this.asks = this.asks.slice(0, this.depth);
    this.bids = this.bids.slice(0, this.depth);
    this.publishOrderbook();
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
  };

  static init = async (args: IInitArgs) => {
    const instance = new BiconomyStrategy(args);

    await instance.fetch();

    return instance;
  };
}
